import type { ReactNode } from "react";
import { ProjectLink } from "./ProjectLink";
import { fmtSite } from "../helpers/format";
import type { Project, Site } from "../api-models";

interface SiteLabelProps {
  site?: Site | null;
  /** The document's own project when it carries one; otherwise the site's preloaded one. */
  project?: Project | null;
  /** Rendered when there is no site — "Central" on documents that can sit at the warehouse. */
  fallback?: ReactNode;
}

/**
 * A site name never stands alone: it is shown under the project it belongs to, with the
 * project part linking through (ProjectLink). Falls back to the bare name if the project
 * wasn't loaded, so a missing preload degrades rather than blanks the label out.
 */
export function SiteLabel({ site, project, fallback = null }: SiteLabelProps) {
  if (!site) return <>{fallback}</>;
  const owner = project ?? site.project;
  if (!owner) return <>{site.name}</>;
  return (
    <>
      <ProjectLink project={owner} /> / {site.name}
    </>
  );
}

// The plain-text form of the same label, for CSV exports (helpers/csv.ts).
SiteLabel.csvText = ({ site, project, fallback }: SiteLabelProps) =>
  site ? fmtSite(project ? { ...site, project } : site) : fallback;
